import * as React from 'react';

interface ColumnSizingInput {
  width: number;      // Initial pixel width
  minWidth?: number;  // Optional minimum pixel width
}

function areArraysEqual(arr1: string[], arr2: string[]): boolean {
  return arr1.length === arr2.length && arr1.every((val, i) => val === arr2[i]);
}

export default function useScaledTableColumnWidths({
  columnWidths,
  containerRef,
  maxTableWidth,
  minTableWidth,
  additionalWidthRestrictions=0
}: {
  columnWidths: ColumnSizingInput[];
  containerRef?: React.RefObject<HTMLElement>;
  maxTableWidth?: number;
  minTableWidth?: number;
  additionalWidthRestrictions?: number; // can be used to account for padding, borders, anything that restricts the actual displayable width of the table
}): string[] {
  const [scaledWidths, setScaledWidths] = React.useState<string[]>(
    columnWidths.map(({ width }) => `${width}px`)
  );

  React.useEffect(() => {
    const updateWidths = () => {
      const containerWidth = (containerRef?.current?.offsetWidth || window.innerWidth) - additionalWidthRestrictions;
      const clampedWidth = Math.max(Math.min(containerWidth, maxTableWidth), minTableWidth);
      const scaleFactor = clampedWidth / maxTableWidth;

      const lockedWidths: Record<number, number> = {};
      const scalableColumns: { index: number; baseWidth: number }[] = [];

      columnWidths.forEach(({ width, minWidth }, index) => {
        const scaled = width * scaleFactor;
        const shouldLock = minWidth !== undefined && scaled <= minWidth;

        if (shouldLock) {
          lockedWidths[index] = minWidth!;
        } else {
          scalableColumns.push({ index, baseWidth: width });
        }
      });

      const lockedTotal = Object.values(lockedWidths).reduce((sum, w) => sum + w, 0);
      const availableSpace = clampedWidth - lockedTotal;
      const scalableBaseTotal = scalableColumns.reduce((sum, col) => sum + col.baseWidth, 0);

      const newWidths: string[] = [];

      columnWidths.forEach(({ width, minWidth }, index) => {
        if (lockedWidths[index] !== undefined) {
          newWidths[index] = `${lockedWidths[index]}px`;
        } else {
          const proportion = width / scalableBaseTotal;
          const scaledWidth = availableSpace * proportion;
          const final = minWidth ? Math.max(scaledWidth, minWidth) : scaledWidth;
          newWidths[index] = `${final}px`;
        }
      });

      if (!areArraysEqual(newWidths, scaledWidths)) {
        setScaledWidths(newWidths);
      }
    };

    updateWidths();
    window.addEventListener('resize', updateWidths);
    return () => window.removeEventListener('resize', updateWidths);
  }, [columnWidths, containerRef?.current, maxTableWidth, minTableWidth, scaledWidths]);

  return scaledWidths;
}
